import { EnergyType } from 'types/producer'
import { SimulationStore } from 'store/simulation/types'
import { Language } from 'types/language'
import { APIProducer, CapacityState, Producer, State } from 'types/producer'
import { readCookie } from 'utils/cookies'
import { getTotalElectricityVolumeForMeterType } from 'utils/helpers'
import { CookieKeys } from 'types/cookies'

/**
 * Fetches the capacity state for a given producer
 *
 * @param producer
 * @param simulation
 * @returns CapacityState
 */
export const getCapacityState = (producer: Producer, simulation: SimulationStore): CapacityState => {
  const usedEnergy = producer.usedCapacity > 100 ? 100 : Math.round(producer.usedCapacity)

  // If producer is marked as sold out in CMS
  if (producer.state === State.SOLD_OUT || usedEnergy === 100) {
    return { soldOut: true, almostSoldOut: true, usedEnergy: 100 }
  }

  // If producer is marked as reserved in CMS
  // (unless user has unlocked producer using ?unlock=id)
  if (isReserved(producer)) {
    return { soldOut: false, almostSoldOut: false, usedEnergy: 100 }
  }

  // If user did no simulation, assume 3500 kWh
  let volume = 3500
  if (simulation && simulation.didSimulation && simulation.price && simulation.price.usage) {
    // If user did simulation, take sum of usages based on meter type
    volume = getTotalElectricityVolumeForMeterType(simulation.inputs.meterType, simulation.price.usage)
  }

  const used = Math.round(((producer.energyUsed + producer.energyReserved + volume) / producer.maxCapacity) * 100)

  // Use minimum percentage from CMS for display
  // We don't want to show 0%
  let visiblePercentage = producer.minOccupation
  if (used > producer.minOccupation) {
    // If the actual used capacity is higher than the CMS value, use the calculated one
    visiblePercentage = used
  }
  // User is allowed to sign up if less than 100% used
  // Make sure the visible percentage is not above 100
  return { soldOut: used >= 100, almostSoldOut: used >= 80, usedEnergy: Number(Math.min(visiblePercentage, 100).toFixed(0)) }
}

/**
 * Returns the i18n key for given energy type
 *
 * @param energyType
 * @returns string
 */
export const getEnergyNameI18Key = (energyType: string): string => {
  switch (energyType) {
    case EnergyType.ZON:
      return 'solar'

    case EnergyType.WATER:
      return 'water'

    case EnergyType.WIND:
      return 'wind'

    case EnergyType.BIO:
      return 'bio'

    default:
      return ''
  }
}

/**
 * Checks if a given producer is reserved
 *
 * @param producer
 * @returns boolean
 */
export const isReserved = (producer: Producer): boolean => {
  if (producer.state !== State.RESERVED) {
    return false
  }

  const unlockedProducerId = readCookie(CookieKeys.UNLOCK)

  if (!unlockedProducerId) {
    return true
  }

  return producer.id !== String(unlockedProducerId)
}

const isSoldOutWithCapacity = (producer: APIProducer): boolean => {
  // assume 3500 kWh
  const volume = 3500
  const used = Math.round(((producer.energyUsed + producer.energyReserved + volume) / producer.maxCapacity) * 100)

  return used >= 100
}

/**
 * Parse a given APIProducer object to a Producer object
 *
 * @param APIProducer producer
 * @returns Producer
 */
export const parseProducer = (producer: APIProducer): Producer => {
  return {
    id: producer.id,
    salesforceId: producer.salesforceId,
    name: {
      nl: producer.translations[Language.DUTCH].name,
      fr: producer.translations[Language.FRENCH].name
    },
    // Fetch the dutch slug only as it is the same for every language
    slug: producer.translations[Language.DUTCH].slug,
    location: producer.location,
    image: producer.image.profile?.url?.replace('upload/', 'upload/c_fill,w_800,') || '',
    header: producer.image.header?.url?.replace('upload/', 'upload/c_fill,w_2500,') || '',
    description: {
      nl: producer.translations[Language.DUTCH].description,
      fr: producer.translations[Language.FRENCH].description
    },
    longDescription: {
      nl: producer.translations[Language.DUTCH].longDescription,
      fr: producer.translations[Language.FRENCH].longDescription
    },
    energyType: producer.energyType as EnergyType,
    energySourceRegion: producer.energyRegion,
    positions: producer.addresses.map((pos) => ({ lat: parseFloat(pos.lat), lng: parseFloat(pos.lng) })),
    energyUsed: producer.energyUsed,
    energyReserved: producer.energyReserved,
    facts: {
      nl: producer.translations[Language.DUTCH].facts,
      fr: producer.translations[Language.FRENCH].facts
    },
    dateAdded: '',
    isFeatured: {
      nl: producer.isFeatured.nl,
      fr: producer.isFeatured.fr
    },
    isPopular: {
      nl: producer.isPopular.nl,
      fr: producer.isPopular.fr
    },
    translations: producer.translations,
    maxCapacity: producer.maxCapacity,
    minOccupation: producer.minOccupation,
    usedCapacity: producer.currentCapacity,
    isSoldOut: producer.state === State.SOLD_OUT,
    state: producer.state,
    order: producer.order,
    capacityState: {
      soldOut: isSoldOutWithCapacity(producer)
    }
  }
}

/**
 * Parses the given API producers to producer objects for the website
 *
 * @param producers
 * @param language
 * @returns Producer[]
 */
export const parseProducers = (producers: APIProducer[], language: Language): Producer[] => {
  const parsedProducers = producers.filter((p) => p.enabled).map((p) => parseProducer(p))
  const popularProducers = parsedProducers.filter((p) => p.isPopular[language] && !p.capacityState.soldOut)
  const soldOutProducers = parsedProducers.filter((p) => p.capacityState.soldOut).sort((p1, p2) => p1.usedCapacity - p2.usedCapacity)
  const otherProducers = parsedProducers
    .filter((p) => !p.capacityState.soldOut && !p.isPopular[language])
    .sort((p1: Producer, p2: Producer) => {
      return p1.order < p2.order ? -1 : p1.order > p2.order ? 1 : 0
    })

  return [...popularProducers, ...otherProducers, ...soldOutProducers]
}
